import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useProductClientSyncConfig } from '../../hooks/useProductClientSyncConfig';
import {
  syncConfigService,
  type SyncConfigInput,
} from '../../services/product-client-sync-config.service';
import { productClientsService } from '../../services/product-clients.service';

interface Props {
  productId: number;
}

const AUTH_TYPES = ['none', 'bearer', 'basic', 'apiKey'] as const;

export function SyncConfigTab({ productId }: Props) {
  const { data, isLoading } = useProductClientSyncConfig(productId);
  const queryClient = useQueryClient();
  const existing = data?.data ?? null;

  const [form, setForm] = useState<SyncConfigInput>({
    endpoint: '',
    auth: { type: 'none' },
    syncIntervalMinutes: 60,
    active: true,
  });

  useEffect(() => {
    if (existing) {
      setForm({
        endpoint: existing.endpoint,
        auth: existing.authJson ?? { type: 'none' },
        syncIntervalMinutes: existing.syncIntervalMinutes,
        active: existing.active,
      });
    }
  }, [existing]);

  const upsertMutation = useMutation({
    mutationFn: () => syncConfigService.upsert(productId, form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-client-sync-config', productId] });
      toast.success('Configuração salva');
    },
    onError: () => toast.error('Erro ao salvar configuração'),
  });

  const syncNowMutation = useMutation({
    mutationFn: () => productClientsService.sync(productId),
    onSuccess: (r: any) => {
      queryClient.invalidateQueries({ queryKey: ['product-client-sync-config', productId] });
      toast.success(`Sync executado: ${r.created} criados, ${r.updated} atualizados`);
    },
    onError: () => toast.error('Erro ao sincronizar'),
  });

  if (isLoading) {
    return <p className="text-ink-tertiary">Carregando...</p>;
  }

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-ink-primary">Sync de Clientes</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label">Endpoint</label>
          <input
            className="input w-full"
            value={form.endpoint}
            onChange={(e) => setForm({ ...form, endpoint: e.target.value })}
            placeholder="https://api.produto.com/clients"
          />
        </div>
        <div>
          <label className="label">Intervalo (min)</label>
          <input
            className="input w-full"
            type="number"
            min={1}
            value={form.syncIntervalMinutes}
            onChange={(e) => setForm({ ...form, syncIntervalMinutes: Number(e.target.value) })}
          />
        </div>
      </div>

      <div>
        <label className="label">Tipo de Autenticação</label>
        <select
          className="input w-full md:w-auto"
          value={form.auth.type}
          onChange={(e) =>
            setForm({
              ...form,
              auth: { type: e.target.value as (typeof AUTH_TYPES)[number] },
            })
          }
        >
          {AUTH_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {form.auth.type === 'bearer' && (
        <div>
          <label className="label">Token</label>
          <input
            className="input w-full"
            type="password"
            placeholder={existing ? '•••••• (deixe vazio para manter)' : ''}
            onChange={(e) =>
              setForm({ ...form, auth: { ...form.auth, type: 'bearer', token: e.target.value } })
            }
          />
        </div>
      )}
      {form.auth.type === 'basic' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Username</label>
            <input
              className="input w-full"
              value={form.auth.username ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  auth: { ...form.auth, type: 'basic', username: e.target.value },
                })
              }
            />
          </div>
          <div>
            <label className="label">Password</label>
            <input
              className="input w-full"
              type="password"
              placeholder={existing ? '•••••• (deixe vazio para manter)' : ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  auth: { ...form.auth, type: 'basic', password: e.target.value },
                })
              }
            />
          </div>
        </div>
      )}
      {form.auth.type === 'apiKey' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Header Name</label>
            <input
              className="input w-full"
              value={form.auth.headerName ?? 'X-API-Key'}
              onChange={(e) =>
                setForm({
                  ...form,
                  auth: { ...form.auth, type: 'apiKey', headerName: e.target.value },
                })
              }
            />
          </div>
          <div>
            <label className="label">Value</label>
            <input
              className="input w-full"
              type="password"
              placeholder={existing ? '•••••• (deixe vazio para manter)' : ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  auth: { ...form.auth, type: 'apiKey', value: e.target.value },
                })
              }
            />
          </div>
        </div>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.active}
          onChange={(e) => setForm({ ...form, active: e.target.checked })}
        />
        Ativo
      </label>

      <div className="flex gap-2">
        <button
          onClick={() => upsertMutation.mutate()}
          disabled={!form.endpoint || upsertMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-ink-primary text-canvas rounded-lg disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> Salvar
        </button>
        <button
          onClick={() => syncNowMutation.mutate()}
          disabled={!existing || syncNowMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-border rounded-lg hover:bg-subtle disabled:opacity-40"
        >
          <RefreshCw className={`w-4 h-4 ${syncNowMutation.isPending ? 'animate-spin' : ''}`} />
          Sincronizar agora
        </button>
      </div>

      {existing && (
        <div className="bg-canvas rounded-lg border border-border p-4 text-sm space-y-1">
          <p className="text-ink-tertiary">
            Último sync: <span className="text-ink-primary">{existing.lastSyncAt ?? '—'}</span>
          </p>
          <p className="text-ink-tertiary">
            Status: <span className="text-ink-primary">{existing.lastSyncStatus ?? '—'}</span>
          </p>
          {existing.lastSyncError && <p className="text-error text-xs">{existing.lastSyncError}</p>}
        </div>
      )}
    </div>
  );
}
